import { Box, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { useState } from 'react'
import { useNotify, useRedirect } from 'react-admin'
import { Form } from 'react-final-form'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { Team as TeamV3 } from 'shared/models/team'
import { User } from 'shared/models/user'

import { moveUserTeam } from '../../api/core'
import { hasPermission, Permissions } from '../../Permissions'
import { TeamReferenceDropdown } from '../Fields/TeamReferenceDropdown'

interface UserTeamMoveProps {
  record: User
  team?: TeamV3
  children: (handleTeamChange?: Handler) => React.ReactNode
  permissions: any
}

export function UserTeamMove({ record, team, children, permissions }: UserTeamMoveProps) {
  const [openChangeTeamDialog, setOpenChangeTeamDialog] = useState(false)
  const [confirmTeamMove, setConfirmTeamMove] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<TeamV3 | null>(team || null)
  const notify = useNotify()
  const redirect = useRedirect()
  const canMoveUser = hasPermission(permissions || [], Permissions.MoveCustomUser)

  const handleChangeTeamClick = () => {
    setOpenChangeTeamDialog(true)
  }

  const handleSelectTeam = (team: TeamV3 | null) => {
    setSelectedTeam(team)
  }

  const handleSave = async (user: User) => {
    moveUserTeam(user.id, selectedTeam?.id)
      .then((response) => {
        notify('User moved!', { type: 'success' })
        setOpenChangeTeamDialog(false)
        const newUserId = response.redirect_url.match(/users\/(.*)\/show/)[1]
        redirect(`/users/${newUserId}/show`)
        window.location.reload()
      })
      .catch((error: any) => {
        notify(JSON.stringify(error.response.data), { type: 'error' })
      })
  }

  const handleCancelTeamMoveModal = () => {
    setOpenChangeTeamDialog(false)
    setConfirmTeamMove(false)
    setSelectedTeam(null)
  }

  const initialValues = {
    user: record,
    team: null,
  }

  return (
    <>
      {children(canMoveUser ? handleChangeTeamClick : undefined)}
      <Dialog open={openChangeTeamDialog} onClose={() => setOpenChangeTeamDialog(false)}>
        <DialogContent>
          <Form
            initialValues={initialValues}
            onSubmit={() => handleSave(record)}
            render={(formProps) => {
              return (
                <>
                  {confirmTeamMove && !!selectedTeam ? (
                    <>
                      <DialogContent>
                        <Typography variant="h5">
                          Confirm Move from {team?.name} to {selectedTeam.name}
                        </Typography>
                        <Text variant="paragraph2">{selectedTeam.id}</Text>
                        <Text variant="paragraph2">DOT #: {selectedTeam.dotNumber}</Text>
                        <Text variant="paragraph2">MC #: {selectedTeam.mcNumber}</Text>
                      </DialogContent>
                      <DialogActions>
                        <Button variant="text" onClick={() => handleCancelTeamMoveModal()}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={formProps.handleSubmit}
                          color="primary"
                          type="submit"
                        >
                          Confirm
                        </Button>
                      </DialogActions>
                    </>
                  ) : (
                    <>
                      <DialogContent>
                        <Typography variant="h5"> Change Team </Typography>
                        <Box mt={2} />
                        <TeamReferenceDropdown onChange={handleSelectTeam} value={selectedTeam} />
                      </DialogContent>
                      <DialogActions>
                        <Button variant="text" onClick={() => setConfirmTeamMove(true)}>
                          Confirm
                        </Button>
                        <Button variant="text" onClick={() => handleCancelTeamMoveModal()}>
                          Cancel
                        </Button>
                      </DialogActions>
                      <style>
                        {`
                          .MuiDialog-paper,
                          .MuiDialogContent-root {
                            overflow: visible;
                          }

                          .MuiDialog-paper {
                            width: 400px;
                          }
                        `}
                      </style>
                    </>
                  )}
                </>
              )
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
